import { vaccinationsService } from "@/services/vaccinations.service";
import { IVaccination } from "@/types/vaccination";
import { useCallback, useState } from "react";

export const useVaccinations = (memberId: string) => {
  const [vaccinations, setVaccinations] = useState<IVaccination[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchVaccinations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await vaccinationsService.getVaccinations(memberId);
      setVaccinations(response);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setError(err instanceof Error ? err.message : 'Failed to fetch vaccinations');
    }
  }, [memberId]);

  const getVaccination = async (vaccinationId: string): Promise<IVaccination> => {
    setLoading(true);
    setError(null);
    try {
      const response = await vaccinationsService.getVaccination(memberId, vaccinationId);
      return response;
    } catch (err) {
      setLoading(false);
      setError(err instanceof Error ? err.message : 'Failed to fetch vaccination');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const addVaccination = async (vaccination: IVaccination): Promise<IVaccination> => {
    setLoading(true);
    setError(null);
    try {
      const response = await vaccinationsService.addVaccination(memberId, vaccination);
      setVaccinations([...vaccinations, response]);
      return response;
    } catch (err) {
      setLoading(false);
      setError(err instanceof Error ? err.message : 'Failed to add vaccination');
    }
  };

  const updateVaccination = async (vaccination: IVaccination) => {
    if (!vaccination.uid) {
      setError('Cannot update vaccination without ID');
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      await vaccinationsService.updateVaccination(memberId, vaccination.uid, vaccination);
      fetchVaccinations();
    } catch (err) {
      setLoading(false);
      setError(err instanceof Error ? err.message : 'Failed to update vaccination');
    }
  };

  const deleteVaccination = async (vaccinationId: string) => {
    setLoading(true);
    setError(null);
    try {
      await vaccinationsService.deleteVaccination(memberId, vaccinationId);
      await fetchVaccinations();
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setError(err instanceof Error ? err.message : 'Failed to delete vaccination');
    }
  };

  const markAsDone = async (vaccinationId: string) => {
    setLoading(true);
    setError(null);
    try {
      await vaccinationsService.markAsDone(memberId, vaccinationId);
      await fetchVaccinations();
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setError(err instanceof Error ? err.message : 'Failed to mark vaccination as done');
    }
  };
  
  return {
    vaccinations,
    loading,
    error,
    fetchVaccinations,
    getVaccination,
    addVaccination,
    updateVaccination,
    deleteVaccination,
    markAsDone
  };
}